import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Card,
    CardContent,
    Alert,
    LinearProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    CleaningServices,
    PlayArrow,
    Warning,
    CheckCircle,
    Error,
    Refresh,
    Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminApiClient } from '../utils/adminApiClient';
import { useSnackbar } from 'notistack';

interface CleanupResult {
    appUsers: {
        count: number;
        deleted: number;
        items: any[];
    };
    accounts: {
        count: number;
        deleted: number;
        items: any[];
    };
    clients: {
        count: number;
        deleted: number;
        items: any[];
    };
    invitations: {
        count: number;
        deleted: number;
        items: any[];
    };
    securityInformation: {
        count: number;
        deleted: number;
        items: any[];
    };
    summary: {
        totalOrphanedRecords: number;
        totalDeletedRecords: number;
        dryRun: boolean;
    };
}

export const DataCleanupManagement: FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<CleanupResult | null>(null);
    const [dryRun, setDryRun] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const handleCleanup = async (isDryRun: boolean) => {
        try {
            setLoading(true);
            const cleanupResult = await adminApiClient.performDataCleanup(isDryRun);
            setResult(cleanupResult);
            
            if (isDryRun) {
                enqueueSnackbar('Dry Run abgeschlossen - Keine Daten wurden gelöscht', { variant: 'info' });
            } else {
                enqueueSnackbar('Datenbereinigung erfolgreich abgeschlossen', { variant: 'success' });
            }
        } catch (error) {
            console.error('Error during data cleanup:', error);
            enqueueSnackbar('Fehler bei der Datenbereinigung', { variant: 'error' });
        } finally {
            setLoading(false);
            setConfirmDialogOpen(false);
        }
    };

    const handleDryRun = () => {
        handleCleanup(true);
    };

    const handleActualCleanup = () => {
        setConfirmDialogOpen(true);
    };

    const confirmActualCleanup = () => {
        handleCleanup(false);
    };

    const getStatusColor = (count: number, deleted: number) => {
        if (count === 0) return 'success';
        if (deleted === 0) return 'warning';
        return 'error';
    };

    const getStatusIcon = (count: number, deleted: number) => {
        if (count === 0) return <CheckCircle />;
        if (deleted === 0) return <Warning />;
        return <Error />;
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Breadcrumb */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link 
                    color="inherit" 
                    href="/app/profile" 
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/app/profile');
                    }}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    Profil
                </Link>
                <Link 
                    color="inherit" 
                    href="/app/admin" 
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/app/admin');
                    }}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    Admin Dashboard
                </Link>
                <Typography color="text.primary">Data Cleanup</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CleaningServices sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" component="h1">
                            Data Cleanup
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Bereinigung von verwaisten Datensätzen und Datenbankwartung
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Info Card */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Was ist Data Cleanup?</strong><br />
                    Diese Funktion identifiziert und entfernt verwaiste Datensätze, die auf nicht mehr existierende Entitäten verweisen. 
                    Dies kann passieren, wenn Clients, Rollen oder andere Referenzen gelöscht werden, aber die abhängigen Datensätze bestehen bleiben.
                </Typography>
            </Alert>

            {/* Action Buttons */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Datenbereinigung durchführen
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={dryRun}
                                onChange={(e) => setDryRun(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Dry Run (nur analysieren, keine Daten löschen)"
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={handleDryRun}
                        disabled={loading}
                        color="primary"
                    >
                        Dry Run starten
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CleaningServices />}
                        onClick={handleActualCleanup}
                        disabled={loading}
                        color="error"
                    >
                        Echte Bereinigung durchführen
                    </Button>
                    {result && (
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={() => setResult(null)}
                        >
                            Ergebnisse zurücksetzen
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Loading Indicator */}
            {loading && <LinearProgress sx={{ mb: 3 }} />}

            {/* Results */}
            {result && (
                <Paper variant="subtle" sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Info />
                        Bereinigungsergebnisse
                    </Typography>
                    
                    {/* Summary */}
                    <Card sx={{ mb: 3, bgcolor: result.summary.dryRun ? 'warning.light' : 'success.light' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Zusammenfassung
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={result.summary.dryRun ? <Warning /> : <CheckCircle />}
                                    label={result.summary.dryRun ? 'Dry Run' : 'Echte Bereinigung'}
                                    color={result.summary.dryRun ? 'warning' : 'success'}
                                />
                                <Chip
                                    label={`${result.summary.totalOrphanedRecords} verwaiste Datensätze gefunden`}
                                    color="info"
                                />
                                <Chip
                                    label={`${result.summary.totalDeletedRecords} Datensätze gelöscht`}
                                    color={result.summary.totalDeletedRecords > 0 ? 'error' : 'default'}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Detailed Results */}
                    <Typography variant="h6" gutterBottom>
                        Detaillierte Ergebnisse
                    </Typography>
                    
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Datentyp</TableCell>
                                    <TableCell align="center">Gefunden</TableCell>
                                    <TableCell align="center">Gelöscht</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CleaningServices />
                                            App Users
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">{result.appUsers.count}</TableCell>
                                    <TableCell align="center">{result.appUsers.deleted}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            icon={getStatusIcon(result.appUsers.count, result.appUsers.deleted)}
                                            label={result.appUsers.count === 0 ? 'Sauber' : result.appUsers.deleted === 0 ? 'Verwaiste Daten' : 'Bereinigt'}
                                            color={getStatusColor(result.appUsers.count, result.appUsers.deleted)}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CleaningServices />
                                            Accounts
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">{result.accounts.count}</TableCell>
                                    <TableCell align="center">{result.accounts.deleted}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            icon={getStatusIcon(result.accounts.count, result.accounts.deleted)}
                                            label={result.accounts.count === 0 ? 'Sauber' : result.accounts.deleted === 0 ? 'Verwaiste Daten' : 'Bereinigt'}
                                            color={getStatusColor(result.accounts.count, result.accounts.deleted)}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CleaningServices />
                                            Clients
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">{result.clients.count}</TableCell>
                                    <TableCell align="center">{result.clients.deleted}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            icon={getStatusIcon(result.clients.count, result.clients.deleted)}
                                            label={result.clients.count === 0 ? 'Sauber' : result.clients.deleted === 0 ? 'Verwaiste Daten' : 'Bereinigt'}
                                            color={getStatusColor(result.clients.count, result.clients.deleted)}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CleaningServices />
                                            Invitations
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">{result.invitations.count}</TableCell>
                                    <TableCell align="center">{result.invitations.deleted}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            icon={getStatusIcon(result.invitations.count, result.invitations.deleted)}
                                            label={result.invitations.count === 0 ? 'Sauber' : result.invitations.deleted === 0 ? 'Verwaiste Daten' : 'Bereinigt'}
                                            color={getStatusColor(result.invitations.count, result.invitations.deleted)}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CleaningServices />
                                            Security Information
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">{result.securityInformation.count}</TableCell>
                                    <TableCell align="center">{result.securityInformation.deleted}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            icon={getStatusIcon(result.securityInformation.count, result.securityInformation.deleted)}
                                            label={result.securityInformation.count === 0 ? 'Sauber' : result.securityInformation.deleted === 0 ? 'Verwaiste Daten' : 'Bereinigt'}
                                            color={getStatusColor(result.securityInformation.count, result.securityInformation.deleted)}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Echte Datenbereinigung bestätigen</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Achtung!</strong> Sie sind dabei, eine echte Datenbereinigung durchzuführen. 
                            Dies wird verwaiste Datensätze permanent aus der Datenbank löschen.
                        </Typography>
                    </Alert>
                    <Typography variant="body2">
                        Diese Aktion kann nicht rückgängig gemacht werden. 
                        Es wird empfohlen, zuerst einen Dry Run durchzuführen, um zu sehen, welche Daten betroffen sind.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button 
                        onClick={confirmActualCleanup} 
                        color="error" 
                        variant="contained"
                        disabled={loading}
                    >
                        Bereinigung durchführen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
